import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { queryClient } from './api/queryClient';
import { AuthProvider } from './auth/AuthContext';
import { AuthSessionBridge } from './components/auth/AuthSessionBridge';
import { RequireAuth, RequireStaff } from './components/auth/ProtectedRoute';
import { FeedbackProvider } from './components/common/FeedbackProvider';
import { InstallPwaBanner } from './components/pwa/InstallPwaBanner';
import { IosPwaSetupDialog } from './components/pwa/IosPwaSetupDialog';
import { PushToastListener } from './components/push/PushToastListener';
import { AdminPage } from './pages/admin/AdminPage';
import { ArticleEditorPage } from './pages/admin/ArticleEditorPage';
import { AlertsPage } from './pages/AlertsPage';
import { ArticlePage } from './pages/ArticlePage';
import { FeedPage } from './pages/FeedPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProfilePage } from './pages/ProfilePage';
import theme from './theme';

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider
          maxSnack={4}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <AuthProvider>
            <FeedbackProvider>
              <BrowserRouter>
                <AuthSessionBridge />
                <PushToastListener />
                <InstallPwaBanner />
                <IosPwaSetupDialog />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <FeedPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/article/:id"
                    element={
                      <RequireAuth>
                        <ArticlePage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/alerts"
                    element={
                      <RequireAuth>
                        <AlertsPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={<Navigate to="/alerts" replace />}
                  />
                  <Route
                    path="/profile"
                    element={
                      <RequireAuth>
                        <ProfilePage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/admin/content/new"
                    element={
                      <RequireStaff>
                        <ArticleEditorPage />
                      </RequireStaff>
                    }
                  />
                  <Route
                    path="/admin/content/:id/edit"
                    element={
                      <RequireStaff>
                        <ArticleEditorPage />
                      </RequireStaff>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <RequireStaff>
                        <AdminPage />
                      </RequireStaff>
                    }
                  />
                  <Route
                    path="/admin/:tab"
                    element={
                      <RequireStaff>
                        <AdminPage />
                      </RequireStaff>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </BrowserRouter>
            </FeedbackProvider>
          </AuthProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
